import { PolicyType } from "@wingspanhq/benefits/dist/src/lib/interfaces";
import {
  WorkInjuryAcceptedCoverageType,
  WorkInjuryEmployerLiabilityBundle
} from "@wingspanhq/benefits/dist/src/lib/interfaces/insuranceRequirement";
import { GeneralRequirementType } from "@wingspanhq/payments/dist/interfaces/requirement";
import {
  BackgroundCheckConfiguration,
  BackgroundCheckConfigurationsApiCreateBackgroundCheckConfigurationRequest,
  DocumentTemplate,
  DocumentTemplatesApiCreateDocumentTemplateRequest,
  InsuranceMonitorConfiguration,
  InsuranceMonitorConfigurationsApiCreateInsuranceMonitorConfigurationRequest,
  RequirementExpirationStrategy,
  RequirementReviewPolicy,
  RequirementType,
  SharedFileRequestConfiguration,
  SharedFileRequestConfigurationsApiCreateSharedFileRequestConfigurationRequest,
  SharedFileRequestContextType
} from "@wingspanhq/wingspan-api-sdk";

import { CreateRequirementDefinitionSharedFileMimeType } from "../Requirements/modules/SharedFile/types";

export type EditableRequirementsTypes = Exclude<
  RequirementType,
  GeneralRequirementType
>;

/**
 * Registration / PayoutMethod / TaxVerification are part of onboarding, not of
 * the engagement requirement catalogue.
 */
export const GENERAL_REQUIREMENT_TYPES: RequirementType[] = [
  RequirementType.REGISTRATION,
  RequirementType.PAYOUT_METHOD,
  RequirementType.TAX_VERIFICATION
];

/**
 * The runtime twin of {@link EditableRequirementsTypes} — every requirement type
 * a payer can attach to an engagement. Pass it as the v3 list filter
 * `type: { anyOf }`, which is how the server-side exclusion v1 expressed as
 * `requirementType: { notIn: GENERAL }` is spelled on v3. Derived from the enum
 * rather than hard-coded so a new SDK type is included automatically, exactly as
 * `notIn` behaved.
 */
export const ENGAGEMENT_REQUIREMENT_TYPES: RequirementType[] = Object.values(
  RequirementType
).filter(type => !GENERAL_REQUIREMENT_TYPES.includes(type));

// v3 create takes only the data-source id — the resource kind is implied by
// the requirement type, so v1 `dataSourceType` has no v3 counterpart.
export type BaseDataSourceResponse = {
  dataSourceId: string;
};

export type RequirementDataSourceCreationRequest =
  | DocumentTemplatesApiCreateDocumentTemplateRequest
  | SharedFileRequestConfigurationsApiCreateSharedFileRequestConfigurationRequest
  | InsuranceMonitorConfigurationsApiCreateInsuranceMonitorConfigurationRequest
  | BackgroundCheckConfigurationsApiCreateBackgroundCheckConfigurationRequest;

export type BaseRequirementDefinitionFormValues = {
  name: string;
  type: RequirementType;
  description?: string;
  isApprovalRequired: boolean;
  reviewStrategy?: RequirementReviewPolicy;
  expirationStrategy?: RequirementExpirationStrategy;
  expirationDays?: number;
  manualRenewalAllowed?: boolean;
  // TODO:?
  instructions?: string;
};

export type RequirementDefinitionFormValues =
  BaseRequirementDefinitionFormValues &
    (
      | RequirementDefinitionSignatureFormValues
      | RequirementDefinitionSharedFileFormValues
      | RequirementDefinitionWorkInjuryFormValues
      | RequirementDefinitionGeneralLiabilityFormValues
      | RequirementDefinitionStandardBackgroundCheckFormValues
      | RequirementDefinitionAutoLiabilityFormValues
      | RequirementDefinitionSmartSharedFileFormValues
      | RequirementDefinitionProfessionalLiabilityFormValues
      | RequirementDefinitionStandardIdentityVerificationFormValues
    );

export type RequirementDefinitionSignatureFormValues = {
  fileId: string;
  signedDocumentTemplateId: string;
  requiredSignatures: "collaborator" | "mutual";
};

export type RequirementDefinitionSharedFileFormValues = {
  instructions?: string;
  contextType?: SharedFileRequestContextType;
  mimeTypes: Partial<
    Record<CreateRequirementDefinitionSharedFileMimeType, boolean>
  >;
};

export type RequirementDefinitionWorkInjuryFormValues = {
  acceptWingspanMasterPolicy: boolean;
  coverageType: WorkInjuryAcceptedCoverageType;
  workersCompLimitMinimum?: WorkInjuryEmployerLiabilityBundle;
  occAccidentMedicalMinimum?: string;
  occAccidentDeathMinimum?: string;
};

export type RequirementDefinitionStandardBackgroundCheckFormValues = {
  package: string;
  instructions?: string;
};

export type RequirementDefinitionGeneralLiabilityFormValues = {
  acceptWingspanMasterPolicy: boolean;
  coverageType: "occurrence" | "claimsMade";
  perIncidentLimitMinimum: string;
  totalCoverageLimitMinimum: string;
};

export type RequirementDefinitionAutoLiabilityFormValues = {
  coverageType: PolicyType[];
  coverageLimitMinimum: string;
  waiverOfSubrogation: boolean;
};

export type RequirementDefinitionSmartSharedFileFormValues = {};

export type RequirementDefinitionProfessionalLiabilityFormValues = {
  acceptWingspanMasterPolicy: boolean;
  perClaimLimitMinimum: string;
  totalCoverageLimitMinimum: string;
  contractorDeductibleMaximum: string;
};
export type RequirementDefinitionStandardIdentityVerificationFormValues = {};

export type RequirementDefinitionSettingsItem =
  | SharedFileRequestConfiguration
  | BackgroundCheckConfiguration
  | DocumentTemplate
  | InsuranceMonitorConfiguration;
